import React, { useEffect, useRef, useState, useCallback } from "react";
import { createConvergenceScene, CONVERGENCE_DEFAULTS } from "../lab/convergence/convergenceScene";
import InstrumentOverlay from "../lab/InstrumentOverlay";

/**
 * Presentation shell.
 *
 * Route: /present
 *
 * A tiny state machine that runs the acts in order — Frame 001,
 * Frame 002, Convergence, SONMI (placeholder) — with a brief black
 * reset between them. No debug panels. No lab controls. The scene
 * modules from the labs are reused verbatim so nothing about Frame
 * 002's frozen mechanics can drift here.
 *
 * Controls
 *   F           fullscreen
 *   Space       (re)play the current act's scene
 *   →           next act (with black reset)
 *   ←           previous act (with black reset)
 *   R           restart the current act
 *   I           toggle instrumentation overlay
 *   N           toggle presenter notes (presenter-only)
 *   Esc         exit fullscreen (browser default)
 *
 * The black between acts is not a fade. It is a perceptual reset —
 * a beat of complete black — so each act can begin with a fresh
 * mental model. Crucially: the new scene is constructed *under*
 * the black, given a few frames to warm shaders and settle its
 * opening pose, and only then is the black lifted. There is a
 * conservative timeout fallback so navigation cannot become
 * trapped on black.
 */

// The black overlay covers the new scene while its shaders compile
// and its opening pose settles. These are budgets, not exact times.
// Frame 001's Voronoi normal-map + PMREM env + physical-material
// shader compile can block the main thread ~800ms on a fresh mount,
// so BLACK_MAX_MS is chosen to always outlast that plus React commit.
const BLACK_MIN_MS = 620;   // never lift the black before this
const BLACK_MAX_MS = 1800;  // always lift the black by this (safety)
const WARM_FRAMES = 4;      // rAF ticks under black before we consider it warm
const PLAY_DELAY_MS = 60;   // small settle after black lifts before playback

const ACTS = [
  // ── The discoveries ──────────────────────────────────────────────
  // Each line answers one question and creates the next. The
  // narration deliberately STOPS before the answer. The visual
  // (a single continuous observation) is the one that reveals it.
  {
    id: "discovery-1",
    label: "The wrong problem",
    type: "narration",
    purpose: "Where the story starts — with a wrong belief.",
    autoplayMs: 11500,
    lines: [
      { text: "I thought I was solving payroll.", holdMs: 3800 },
      { text: "I wasn't.",                        holdMs: 2600 },
    ],
  },
  {
    id: "discovery-2",
    label: "The real difficulty",
    type: "narration",
    purpose: "The setup — the audience should feel a question forming.",
    autoplayMs: 14500,
    lines: [
      { text: "The calculations weren't the problem I was trying to solve.",  holdMs: 4200 },
      { text: "Every role saw only part of the picture.",                     holdMs: 4600 },
    ],
  },
  // ── A single continuous observation ─────────────────────────────
  // The observer is not reset. What was Frame 002 and what was
  // Convergence are treated as two phases of the same seeing:
  // the world reveals its second orb, holds, rotates, settles,
  // and then the relationship itself begins to change. No cut
  // between "here's an object" and "here's another scene". The
  // observer remains present; understanding deepens.
  { id: "observation", label: "Observation", type: "convergence",
    purpose: "The dramatic hinge. Orb B enters. Then, without a cut, the relationship begins to change.",
    autoplayMs: 46000,
    cue: { text: "No role stands alone.", phase: "withdrawing", phaseFraction: 0.55 } },
  // ── The name arrives — inevitable by now ────────────────────────
  { id: "sonmi", label: "SONMI", type: "placeholder",
    purpose: "The audience arrives here with you. The next relationship.",
    autoplayMs: null },
];

// Narration typography timing. Each line: fade in → hold → fade out → beat of black.
const NARRATION_FADE_MS = 800;
const NARRATION_GAP_MS = 600;

// Rest distance between the two objects (both Frame 002 and Convergence).
const REST_DISTANCE_UNIT = Math.sqrt(1.35 * 1.35 + 0.22 * 0.22 + 1.20 * 1.20);


function formatElapsed(ms) {
  if (ms < 0) ms = 0;
  const s = Math.floor(ms / 1000);
  const mm = String(Math.floor(s / 60)).padStart(2, "0");
  const ss = String(s % 60).padStart(2, "0");
  return `${mm}:${ss}`;
}


export default function Presentation() {
  const containerRef = useRef(null);
  const sceneApiRef = useRef(null);

  // Every entry/restart increments this. Callbacks reject themselves
  // if they are running under a stale sequence id — protection
  // against rapid navigation.
  const seqRef = useRef(0);
  const timersRef = useRef(new Set());

  // Presentation Mode vs Story Mode.
  //   /present               → Story Mode (autoplay, no controls, self-contained)
  //   /present?mode=live     → Presentation Mode (manual, notes, keyboard, discussion)
  // These are treated as different products with different defaults.
  // Once decided at mount, the mode does not change during the piece.
  const modeRef = useRef((() => {
    if (typeof window === "undefined") return "story";
    const m = new URLSearchParams(window.location.search).get("mode");
    return m === "live" || m === "presentation" || m === "keynote" ? "live" : "story";
  })());
  const mode = modeRef.current;

  const [actIndex, setActIndex] = useState(0);
  const [runId, setRunId] = useState(0); // bump to force restart of same act
  const [inBlackReset, setInBlackReset] = useState(true); // start under black; first entry warms too
  const [sceneReady, setSceneReady] = useState(false); // deterministic readiness marker
  const [instrumentVisible, setInstrumentVisible] = useState(false);
  const [notesVisible, setNotesVisible] = useState(false);
  const [progress, setProgress] = useState({ state: "idle", phaseT: 0, phaseDuration: 0, phaseFraction: 0 });
  const [activeParams, setActiveParams] = useState(CONVERGENCE_DEFAULTS);
  const [showChrome, setShowChrome] = useState(false);

  // The moment playback begins for the current act, in ms since epoch.
  // Set when api.play() is called (Frame 002 / Convergence) or when
  // the black lifts on Frame 001 (which has no explicit play — it
  // just begins). Null on the SONMI placeholder and while the shell
  // is still under black.
  const [playbackStartMs, setPlaybackStartMs] = useState(null);
  const [now, setNow] = useState(() => performance.now());

  // Narration state — index into the current act's `lines`. Fading
  // in/out is driven by CSS keyframes keyed on this value + a phase.
  const [narrationLineIdx, setNarrationLineIdx] = useState(-1);

  // Presenter cue: a short line the presenter should speak, timed
  // to a specific moment inside a scene (not wall-clock). Fires
  // from the scene's own state machine via onProgress. Only shown
  // in Presentation Mode — Story Mode has no presenter to see it.
  const [cueVisible, setCueVisible] = useState(false);
  const [cueText, setCueText] = useState("");
  const cueFiredRef = useRef(false);

  // Autoplay: does the presentation advance itself when an act ends?
  // Default depends on mode. Presentation Mode expects a presenter
  // driving the pacing — autoplay would fight them.
  const [autoplay, setAutoplay] = useState(mode === "story");

  const currentAct = ACTS[actIndex];

  // Mirror actIndex in a ref so event handlers can compute the next
  // index synchronously and cover the transition with black in the
  // SAME render as the actIndex change.
  const actIndexRef = useRef(actIndex);
  useEffect(() => { actIndexRef.current = actIndex; }, [actIndex]);

  // Mirror autoplay in a ref so the mount effect's scheduled
  // auto-advance timer can read the CURRENT value at fire time
  // (not the value captured when the effect ran).
  const autoplayRef = useRef(autoplay);
  useEffect(() => { autoplayRef.current = autoplay; }, [autoplay]);

  // ---- Timer helpers (all timers cancel on unmount / new sequence)
  const setTrackedTimeout = useCallback((fn, ms) => {
    const id = setTimeout(() => {
      timersRef.current.delete(id);
      fn();
    }, ms);
    timersRef.current.add(id);
    return id;
  }, []);
  const clearAllTimers = useCallback(() => {
    timersRef.current.forEach((id) => clearTimeout(id));
    timersRef.current.clear();
  }, []);

  // Auto-hide the small act indicator after a moment; reveal on mouse
  // motion. Never lets it obstruct the frame during presentation.
  useEffect(() => {
    let t = setTimeout(() => setShowChrome(false), 3000);
    const onMove = () => {
      setShowChrome(true);
      clearTimeout(t);
      t = setTimeout(() => setShowChrome(false), 2400);
    };
    window.addEventListener("mousemove", onMove);
    return () => { window.removeEventListener("mousemove", onMove); clearTimeout(t); };
  }, []);

  // Mount / dispose the scene for the current act.
  // The scene mounts UNDER the black; we wait for a few rAF frames
  // (shader warm) plus a minimum wall-clock beat, then lift the
  // black. A max-timeout guarantees the black always lifts —
  // navigation can never trap on black.
  useEffect(() => {
    seqRef.current += 1;
    const mySeq = seqRef.current;
    const timers = timersRef.current;

    // Cancel any lingering timers from a previous transition.
    clearAllTimers();

    // Show the black overlay for this transition. (Already true on
    // first mount and on advance(); still safe to set here for the
    // restart case where actIndex is unchanged.)
    setInBlackReset(true);
    setSceneReady(false);
    setPlaybackStartMs(null);
    setProgress({ state: "idle", phaseT: 0, phaseDuration: 0, phaseFraction: 0 });

    if (!containerRef.current) return;

    const act = ACTS[actIndex];

    // Reset narration state on every mount / restart.
    setNarrationLineIdx(-1);
    // Reset presenter cue state on every mount / restart.
    setCueVisible(false);
    setCueText("");
    cueFiredRef.current = false;

    // Placeholder acts (SONMI) have no scene, just a still slide
    // that appears once the black lifts. Readiness is still a
    // meaningful signal for automation — the slide has appeared.
    if (act.type === "placeholder") {
      setTrackedTimeout(() => {
        if (seqRef.current !== mySeq) return;
        setInBlackReset(false);
        setSceneReady(true);
      }, BLACK_MIN_MS);
      return;
    }

    // Narration acts (Chapters 1 & 2) — text on black, no scene.
    // Same black-lift discipline so the transition into the first
    // line feels like every other act. Then we sequence the lines
    // via tracked timeouts, so manual navigation cancels them.
    if (act.type === "narration") {
      setTrackedTimeout(() => {
        if (seqRef.current !== mySeq) return;
        setInBlackReset(false);
        setSceneReady(true);
        setPlaybackStartMs(performance.now());

        // Schedule each line's appearance. The `narrationLineIdx`
        // state drives which line is currently on screen; -1 means
        // the space is empty (either before the first line or
        // between lines).
        let t = 400;  // small settle before the first line appears
        act.lines.forEach((line, i) => {
          setTrackedTimeout(() => {
            if (seqRef.current !== mySeq) return;
            setNarrationLineIdx(i);
          }, t);
          // Line visible for its full cycle: fade in + hold + fade out.
          t += NARRATION_FADE_MS + (line.holdMs ?? 3000) + NARRATION_FADE_MS;
          // Between lines, clear the space for a beat of black.
          setTrackedTimeout(() => {
            if (seqRef.current !== mySeq) return;
            setNarrationLineIdx(-1);
          }, t);
          if (i < act.lines.length - 1) t += NARRATION_GAP_MS;
        });
      }, BLACK_MIN_MS);
      // Autoplay advance is scheduled at the bottom of this effect.
      // (Fall through past the WebGL setup — no scene to mount.)
    }

    // WebGL scenes get constructed and warmed below. Non-WebGL acts
    // (narration/placeholder) skip this whole block. The keynote's
    // only WebGL act is now "convergence" — a single continuous
    // observation that plays what were formerly Frame 002 and
    // Convergence as one uninterrupted scene. This preserves the
    // audience's sense of being a continuous observer.
    if (act.type === "convergence") {
      const container = containerRef.current;
      let api = null;
      api = createConvergenceScene({
        container,
        initialParams: CONVERGENCE_DEFAULTS,
        onProgress: (p) => {
          if (seqRef.current !== mySeq) return;
          setProgress(p);
          // Presenter cue — fires from the scene's own state
          // machine when Orb B becomes unmistakable during the
          // withdrawing phase. Once per mount.
          if (
            !cueFiredRef.current &&
            act.cue &&
            p.state === act.cue.phase &&
            p.phaseFraction >= act.cue.phaseFraction
          ) {
            cueFiredRef.current = true;
            setCueText(act.cue.text);
            setCueVisible(true);
            setTrackedTimeout(() => {
              if (seqRef.current !== mySeq) return;
              setCueVisible(false);
            }, 4000);
          }
        },
      });
      setActiveParams(CONVERGENCE_DEFAULTS);

      sceneApiRef.current = api;

      // Warm-under-black: wait for a few rAF ticks AND at least
      // BLACK_MIN_MS, then lift. A hard fallback lifts at BLACK_MAX_MS
      // no matter what.
      const started = performance.now();
      let frames = 0;
      let lifted = false;

      const lift = () => {
        if (lifted) return;
        if (seqRef.current !== mySeq) return;
        lifted = true;
        setInBlackReset(false);
        setSceneReady(true);
        if (!api || typeof api.play !== "function") {
          setPlaybackStartMs(performance.now());
        }
        if (api && typeof api.play === "function") {
          setTrackedTimeout(() => {
            if (seqRef.current !== mySeq) return;
            try { api.play(); } catch (_) { /* scene already gone */ }
            setPlaybackStartMs(performance.now());
          }, PLAY_DELAY_MS);
        }
      };

      const tick = () => {
        if (seqRef.current !== mySeq) return;
        if (lifted) return;
        frames += 1;
        const elapsed = performance.now() - started;
        if (frames >= WARM_FRAMES && elapsed >= BLACK_MIN_MS) {
          lift();
        } else {
          requestAnimationFrame(tick);
        }
      };
      requestAnimationFrame(tick);

      // Safety fallback — always lift by BLACK_MAX_MS.
      setTrackedTimeout(lift, BLACK_MAX_MS);
    }

    // ---- Autoplay auto-advance -----------------------------------
    // If autoplay is on AND the current act has a finite autoplayMs,
    // schedule the transition to the next act. `actIndexRef.current`
    // is read at fire time so we don't advance past the end. This
    // timer is tracked and therefore cancelled when the user
    // navigates manually (advance() / restart() bump the sequence
    // via cleanup, which clears all tracked timers).
    if (autoplayRef.current && typeof act.autoplayMs === "number") {
      setTrackedTimeout(() => {
        if (seqRef.current !== mySeq) return;
        if (!autoplayRef.current) return;
        const cur = actIndexRef.current;
        if (cur < ACTS.length - 1) {
          setInBlackReset(true);
          setSceneReady(false);
          setActIndex(cur + 1);
        }
      }, act.autoplayMs);
    }

    // Cleanup: dispose the scene + cancel timers before the next
    // sequence begins.
    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      timers.forEach((id) => clearTimeout(id));
      timers.clear();
      if (sceneApiRef.current) {
        sceneApiRef.current.dispose();
        sceneApiRef.current = null;
      }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actIndex, runId]);

  // Full cleanup on unmount.
  useEffect(() => {
    return () => {
      clearAllTimers();
      if (sceneApiRef.current) {
        sceneApiRef.current.dispose();
        sceneApiRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Navigation --------------------------------------------------

  const advance = useCallback((delta) => {
    const cur = actIndexRef.current;
    const next = Math.max(0, Math.min(ACTS.length - 1, cur + delta));
    if (next === cur) return;
    // The presenter took the wheel. Stop autoplay for the rest of
    // the piece — no surprise transitions during live delivery.
    setAutoplay(false);
    // Set the black cover + reset readiness IN THE SAME EVENT as the
    // actIndex change so React batches all three into a single
    // render+commit. The first paint after the keypress already has
    // the black overlay in place, guaranteeing the new scene never
    // appears uncovered while it constructs.
    setInBlackReset(true);
    setSceneReady(false);
    setActIndex(next);
  }, []);

  const restart = useCallback(() => {
    // Manual restart also takes the wheel from autoplay.
    setAutoplay(false);
    setInBlackReset(true);
    setSceneReady(false);
    setRunId((n) => n + 1);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  const playCurrent = useCallback(() => {
    if (sceneApiRef.current?.play) {
      sceneApiRef.current.play();
      // Space-replay resets the elapsed clock — measuring the
      // current playthrough, not the presenter's hesitation.
      setPlaybackStartMs(performance.now());
    }
  }, []);

  // Elapsed-time ticker: only spins while notes are visible AND
  // playback has started. When notes are hidden we don't tick,
  // saving renders during the actual presentation.
  useEffect(() => {
    if (!notesVisible || playbackStartMs === null) return;
    const id = setInterval(() => setNow(performance.now()), 500);
    return () => clearInterval(id);
  }, [notesVisible, playbackStartMs]);

  useEffect(() => {
    const onKey = (e) => {
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      // In Story Mode the piece is self-contained: no notes,
      // no instrument overlay, no visible controls hint. Manual
      // navigation (arrows, space, R) is still accepted so a
      // stray keypress doesn't lock the viewer in — but the
      // presenter-only affordances are gone.
      const isLive = modeRef.current === "live";

      if (e.key === "F" || e.key === "f") { e.preventDefault(); toggleFullscreen(); }
      else if (e.key === "ArrowRight") { e.preventDefault(); advance(1); }
      else if (e.key === "ArrowLeft") { e.preventDefault(); advance(-1); }
      else if (e.key === "R" || e.key === "r") { e.preventDefault(); restart(); }
      else if ((e.key === "I" || e.key === "i") && isLive) { e.preventDefault(); setInstrumentVisible((v) => !v); }
      else if ((e.key === "N" || e.key === "n") && isLive) { e.preventDefault(); setNotesVisible((v) => !v); }
      else if (e.code === "Space") { e.preventDefault(); playCurrent(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [advance, restart, toggleFullscreen, playCurrent]);

  const hasProgress = currentAct.type === "frame002" || currentAct.type === "convergence";

  return (
    <div
      data-testid="presentation"
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        overflow: "hidden",
        cursor: showChrome ? "default" : "none",
      }}
    >
      <div
        ref={containerRef}
        data-testid="presentation-canvas"
        style={{ position: "absolute", inset: 0 }}
      />

      {/* Deterministic readiness marker. Not audience-facing.
          Automation waits for
             [data-presentation-ready="true"][data-current-act="…"]
          instead of the browser's load/networkidle event, which
          on this preview environment never settles cleanly due
          to unrelated background polling on other routes.
          1x1 with opacity 0 so Playwright's default visibility
          check (non-zero layout box) passes, without the human
          eye ever seeing it. */}
      <div
        aria-hidden="true"
        data-presentation-ready={sceneReady ? "true" : "false"}
        data-current-act={currentAct.id}
        data-in-black-reset={inBlackReset ? "true" : "false"}
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: 1,
          height: 1,
          opacity: 0,
          pointerEvents: "none",
          zIndex: 0,
        }}
      />

      {/* Narration acts (Chapter 1 & Chapter 2) — a single line at
          a time on black. The fade cadence is CSS-only; each line
          remounts (via `key`) so the animation restarts cleanly.
          Animation duration = fade + hold + fade; the same 20/80
          keyframe percentages produce a proportional fade-hold-fade
          regardless of line length. */}
      {currentAct.type === "narration" && !inBlackReset && narrationLineIdx >= 0 && (() => {
        const holdMs = currentAct.lines[narrationLineIdx]?.holdMs ?? 3000;
        const totalMs = NARRATION_FADE_MS * 2 + holdMs;
        const fadePct = (NARRATION_FADE_MS / totalMs) * 100;
        return (
          <div
            data-testid="narration-line"
            data-line-index={narrationLineIdx}
            key={`${currentAct.id}-${narrationLineIdx}`}
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 8vw",
              color: "rgba(255,255,255,0.78)",
              fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
              fontStyle: "italic",
              fontWeight: 300,
              fontSize: "clamp(1.6rem, 3.2vw, 2.6rem)",
              lineHeight: 1.4,
              letterSpacing: "0.015em",
              textAlign: "center",
              userSelect: "none",
              opacity: 0,
              animation: `narrationFade-${narrationLineIdx} ${totalMs}ms ease-in-out forwards`,
            }}
          >
            <style>{`
              @keyframes narrationFade-${narrationLineIdx} {
                0%   { opacity: 0; }
                ${fadePct.toFixed(2)}%  { opacity: 1; }
                ${(100 - fadePct).toFixed(2)}% { opacity: 1; }
                100% { opacity: 0; }
              }
            `}</style>
            {currentAct.lines[narrationLineIdx].text}
          </div>
        );
      })()}

      {/* SONMI placeholder — an intentional pause slide, held still. */}
      {currentAct.type === "placeholder" && !inBlackReset && (
        <div
          data-testid="sonmi-placeholder"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            color: "rgba(255,255,255,0.72)",
            fontFamily: "'Cormorant Garamond', 'Times New Roman', serif",
            textAlign: "center",
            gap: "0.9rem",
            userSelect: "none",
          }}
        >
          <div
            style={{
              fontSize: "clamp(3.5rem, 9vw, 7rem)",
              fontWeight: 300,
              letterSpacing: "0.24em",
              lineHeight: 1,
            }}
          >
            SONMI
          </div>
          <div
            style={{
              fontSize: "clamp(1rem, 1.8vw, 1.4rem)",
              fontStyle: "italic",
              color: "rgba(255,255,255,0.42)",
              letterSpacing: "0.04em",
            }}
          >
            The next relationship.
          </div>
        </div>
      )}

      {/* Perceptual reset — complete black, not a fade. */}
      {inBlackReset && (
        <div
          data-testid="black-reset"
          style={{ position: "absolute", inset: 0, background: "#000", zIndex: 5 }}
        />
      )}

      {/* Instrument overlay — hidden by default; toggled with I. */}
      {hasProgress && (
        <InstrumentOverlay
          visible={instrumentVisible && !inBlackReset}
          frame={currentAct.id}
          progress={progress}
          params={activeParams}
          restDistance={REST_DISTANCE_UNIT * (activeParams.objectSeparation ?? 1)}
          hasConvergence={currentAct.type === "convergence"}
        />
      )}

      {/* Act indicator + controls hint are Presentation-Mode
          affordances only. Story Mode is self-contained — the
          viewer sees no UI ever. */}
      {mode === "live" && (
        <div
          data-testid="act-indicator"
          style={{
            position: "fixed",
            top: 16,
            left: 20,
            color: "rgba(255,255,255,0.35)",
            fontSize: 10,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            opacity: showChrome ? 1 : 0,
            transition: "opacity 0.6s ease",
            pointerEvents: "none",
            zIndex: 8,
          }}
        >
          {String(actIndex + 1).padStart(2, "0")} / {String(ACTS.length).padStart(2, "0")}
          <span style={{ color: "rgba(255,255,255,0.20)", marginLeft: 12 }}>
            {currentAct.label}
          </span>
        </div>
      )}

      {/* Controls hint — also auto-hides. Presentation Mode only. */}
      {mode === "live" && (
        <div
          data-testid="controls-hint"
          style={{
            position: "fixed",
            bottom: 16,
            right: 20,
            color: "rgba(255,255,255,0.22)",
            fontSize: 9,
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            opacity: showChrome ? 1 : 0,
            transition: "opacity 0.6s ease",
            pointerEvents: "none",
            zIndex: 8,
          }}
        >
          F fullscreen &nbsp; ← → act &nbsp; space play &nbsp; r restart &nbsp; i instrument &nbsp; n notes
        </div>
      )}

      {/* Presenter cue — a timed prompt telling the presenter WHEN
          to speak a specific line during a scene. Fires from the
          scene's own state machine (not wall-clock). Presentation
          Mode only. Positioned top-right, opposite the notes
          overlay, so the presenter can glance at it without moving
          their head far. Amber to match the presenter-affordance
          palette and be immediately distinguishable from audience
          content. Text is what the presenter says, not what the
          audience sees — nothing is drawn over the scene itself. */}
      {mode === "live" && cueVisible && (
        <div
          data-testid="presenter-cue"
          style={{
            position: "fixed",
            top: 20,
            right: 20,
            padding: "12px 16px",
            background: "rgba(60, 34, 6, 0.94)",
            border: "1px solid #f59e0b",
            borderLeft: "4px solid #f59e0b",
            color: "#fde68a",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 11,
            lineHeight: 1.4,
            letterSpacing: "0.02em",
            zIndex: 20,
            maxWidth: 340,
            pointerEvents: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
            animation: "cueFade 4000ms ease-in-out forwards",
          }}
        >
          <div style={{ fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: "#fbbf24", marginBottom: 6, fontWeight: 700 }}>
            → speak now
          </div>
          <div style={{ color: "#fef3c7", fontStyle: "italic", fontSize: 14 }}>
            "{cueText}"
          </div>
          <style>{`
            @keyframes cueFade {
              0%   { opacity: 0; transform: translateY(-4px); }
              8%   { opacity: 1; transform: translateY(0); }
              88%  { opacity: 1; transform: translateY(0); }
              100% { opacity: 0; transform: translateY(-4px); }
            }
          `}</style>
        </div>
      )}

      {/* Presenter Notes — unmistakably not for the audience.
          Warning-colored, prominent border, explicit PRESENTER tag,
          and a visible instruction to hide it. If it flickers on
          screen accidentally, the presenter (and the audience)
          will see it immediately and reach for N. */}
      {notesVisible && (
        <div
          data-testid="notes-overlay"
          style={{
            position: "fixed",
            left: 16,
            bottom: 44, // sit above the controls hint
            padding: "14px 18px",
            background: "rgba(60, 34, 6, 0.92)",
            border: "1px solid #f59e0b",
            borderLeft: "4px solid #f59e0b",
            color: "#fde68a",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 12,
            lineHeight: 1.5,
            letterSpacing: "0.02em",
            zIndex: 20,
            maxWidth: 460,
            pointerEvents: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.6)",
          }}
        >
          <div
            style={{
              fontSize: 9,
              letterSpacing: "0.24em",
              textTransform: "uppercase",
              color: "#fbbf24",
              marginBottom: 8,
              fontWeight: 700,
            }}
          >
            ⚠ PRESENTER · press N to hide
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 14, rowGap: 4 }}>
            <span style={{ color: "#fbbf24aa" }}>ACT</span>
            <span>
              {String(actIndex + 1).padStart(2, "0")} / {String(ACTS.length).padStart(2, "0")} — {currentAct.label}
            </span>
            <span style={{ color: "#fbbf24aa" }}>NEXT</span>
            <span>
              {actIndex + 1 < ACTS.length
                ? ACTS[actIndex + 1].label
                : "— end —"}
            </span>
            <span style={{ color: "#fbbf24aa" }}>MODE</span>
            <span>
              {mode === "live"
                ? (autoplay ? "presentation · autoplay" : "presentation · manual")
                : (autoplay ? "story · autoplay" : "story · manual (interrupted)")}
            </span>
            <span style={{ color: "#fbbf24aa" }}>ELAPSED</span>
            <span>
              {playbackStartMs === null
                ? "— (not yet playing)"
                : formatElapsed(now - playbackStartMs)}
            </span>
            <span style={{ color: "#fbbf24aa" }}>PURPOSE</span>
            <span style={{ color: "#fef3c7" }}>{currentAct.purpose}</span>
          </div>
        </div>
      )}
    </div>
  );
}
